import { Curriculum, Nullable, PlanScope, PlanSection, PlannerPlan, CurriculumPlan } from '@/lib/types';

// Client-only working copy of a practice plan while it's being edited in the
// builder. Not part of the wire contract — never sent as-is; use-planner-plans
// #savePlan maps this into the PlannerPlan POST/PUT payload.
export interface PlanDraft {
  id?: number;
  workspace_id: number;
  // Which day slot this draft targets: the shared workspace plan or the
  // author's private "My Plan". Chosen before the builder opens (choice
  // screen / scope tabs) and fixed once saved — the API ignores scope on
  // update.
  scope: PlanScope;
  name: string;
  plan_date: string; // 'YYYY-MM-DD'
  start_time: string; // 'HH:mm'
  duration_minutes: number;
  goals: string;
  notes: string;
  learning_outcomes: string;
  prerequisites: string;
  sections: PlanSection[];
  from_curriculum: boolean;
  source_curriculum_plan_id: Nullable<number>;
  // True once the coach edits goals/notes/learning_outcomes/prerequisites by
  // hand, so later curriculum re-syncs don't clobber their edits.
  outcomes_locked: boolean;
}

// Stable client-side id generator for sections/items (uid field). Native
// crypto.randomUUID() is available in all supported browsers — no polyfill.
export function uid(): string {
  return crypto.randomUUID();
}

// Deep-copies sections (and their items), regenerating every uid so the copy
// is fully independent of the source — used whenever a draft is derived from
// an existing plan/slot (past plan reuse, curriculum copy) so editing the
// draft never mutates the original's section/item identities.
export function cloneSections(sections: PlanSection[]): PlanSection[] {
  return sections.map((section) => ({
    ...section,
    uid: uid(),
    items: section.items.map((item) => ({ ...item, uid: uid() })),
  }));
}

// Empty section with defaults — used both to seed a blank draft's first
// section and by the builder's "Add section" button.
export function blankSection(): PlanSection {
  return { uid: uid(), name: 'New section', duration_minutes: 15, notes: '', items: [] };
}

// Blank draft for "Create new plan" on a date without a plan yet. Seeds one
// empty section so the builder opens ready to fill in, not with a bare
// "Add section" button.
export function blankDraft(workspaceId: number, date: string, scope: PlanScope = 'workspace'): PlanDraft {
  return {
    workspace_id: workspaceId,
    scope,
    name: 'New practice plan',
    plan_date: date,
    start_time: '15:00',
    duration_minutes: 60,
    goals: '',
    notes: '',
    learning_outcomes: '',
    prerequisites: '',
    sections: [blankSection()],
    from_curriculum: false,
    source_curriculum_plan_id: null,
    outcomes_locked: false,
  };
}

// "Use & modify past plan" — deep-copy a previously scheduled day plan onto a
// new date. Never carries over the source's id (this always creates a new
// plan) or its curriculum linkage. `scope` is the slot being filled on the
// target date, not the source's scope — copying the team's plan into your
// private "My Plan" (or vice versa) is allowed.
export function draftFromPast(past: PlannerPlan, date: string, scope: PlanScope): PlanDraft {
  return {
    workspace_id: past.workspace_id,
    scope,
    name: past.name,
    plan_date: date,
    start_time: past.start_time,
    duration_minutes: past.duration_minutes,
    goals: past.goals,
    notes: past.notes,
    learning_outcomes: past.learning_outcomes,
    prerequisites: past.prerequisites,
    sections: cloneSections(past.sections),
    from_curriculum: false,
    source_curriculum_plan_id: null,
    outcomes_locked: false,
  };
}

// "Add from Curriculum" — deep-copy a built curriculum slot onto a date.
// learning_outcomes/prerequisites are inherited from the parent `curriculum`
// (slots don't carry them — they describe the curriculum as a whole) and
// snapshotted onto the day plan here at copy time, so the plan keeps rendering
// the text it was built with even if the curriculum is edited later. When
// `locked` is true (day-mode copy) they render read-only in the builder but are
// still saved with the plan. Curriculum-mode editing of the slot itself never
// locks (locked stays false there).
export function draftFromSlot(
  slot: CurriculumPlan,
  curriculum: Pick<Curriculum, 'learning_outcomes' | 'prerequisites'>,
  date: string,
  locked: boolean,
  scope: PlanScope = 'workspace',
): PlanDraft {
  return {
    workspace_id: slot.workspace_id,
    scope,
    name: slot.name,
    plan_date: date,
    start_time: '15:00',
    duration_minutes: slot.duration_minutes,
    goals: slot.goals,
    notes: slot.notes,
    learning_outcomes: curriculum.learning_outcomes,
    prerequisites: curriculum.prerequisites,
    sections: cloneSections(slot.sections),
    from_curriculum: true,
    source_curriculum_plan_id: slot.id,
    outcomes_locked: locked,
  };
}

// Editing an existing, already-saved day plan (from the view screen's Edit
// button). Preserves id/curriculum linkage/lock state as-is — sections are
// still cloned (fresh uids) so in-progress edits never alias the store's copy.
export function draftFromPlan(plan: PlannerPlan): PlanDraft {
  return {
    id: plan.id,
    workspace_id: plan.workspace_id,
    scope: plan.scope,
    name: plan.name,
    plan_date: plan.plan_date,
    start_time: plan.start_time,
    duration_minutes: plan.duration_minutes,
    goals: plan.goals,
    notes: plan.notes,
    learning_outcomes: plan.learning_outcomes,
    prerequisites: plan.prerequisites,
    sections: cloneSections(plan.sections),
    from_curriculum: plan.from_curriculum,
    source_curriculum_plan_id: plan.source_curriculum_plan_id,
    outcomes_locked: plan.from_curriculum,
  };
}

// Client-only working copy of the two-step "create curriculum" wizard: step 1
// (curCreate) collects the scalars, step 2 (curStructure) the plan shape every
// slot starts from. The whole draft rides on the screen state so stepping Back
// keeps what was already typed. Sections never carry items here — content is
// added per plan in the slot builder.
export interface CurriculumDraft {
  name: string;
  description: string;
  // Curriculum-wide, collected on step 1 alongside the description: what
  // athletes need before starting, and what they'll know after finishing.
  learning_outcomes: string;
  prerequisites: string;
  plan_count: number;
  duration_minutes: number;
  sections: PlanSection[];
}

// A section in the shared structure: same shape as a plan section, minus the
// items. Rows start with both fields blank — 0 is the "unset" duration, which
// curStructure renders as an empty input rather than a literal 0.
export function structureSection(name = '', durationMinutes = 0): PlanSection {
  return { uid: uid(), name, duration_minutes: durationMinutes, notes: '', items: [] };
}

// Opening state for the create-curriculum wizard: 4 plans of 90 min, seeded
// with one blank section so step 2 opens ready to type into rather than as a
// bare "Add section" button (same reasoning as blankDraft). Name and duration
// are both left empty — this shape is copied onto every slot, so pre-filling
// either would commit the admin to a practice structure they never chose, and
// curStructure drops still-unnamed rows on save.
export function blankCurriculumDraft(): CurriculumDraft {
  return {
    name: '',
    description: '',
    learning_outcomes: '',
    prerequisites: '',
    plan_count: 4,
    duration_minutes: 90,
    sections: [structureSection()],
  };
}

export interface SectionStartTime {
  sectionUid: string;
  startTime: string; // 'HH:mm'
}

// Formats a canonical 'HH:mm' wire time for display as 12-hour AM/PM
// (e.g. '15:00' → '3:00 PM'). The wire/state format stays 24-hour — only
// render sites use this.
export function formatTime12h(hhmm: string): string {
  const [h, m] = hhmm.split(':').map((part) => parseInt(part, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// Computes each section's cumulative start time from the plan's start_time,
// walking sections in array order and summing prior sections' durations.
export function computeSectionStartTimes(
  startTime: string,
  sections: PlanSection[],
): SectionStartTime[] {
  const [h, m] = startTime.split(':').map((part) => parseInt(part, 10));
  let cursor = (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  const result: SectionStartTime[] = [];
  for (const section of sections) {
    const hh = Math.floor(cursor / 60) % 24;
    const mm = cursor % 60;
    result.push({
      sectionUid: section.uid,
      startTime: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
    });
    cursor += section.duration_minutes || 0;
  }
  return result;
}
